// eslint-disable-next-line boundaries/no-unknown-files
import '@radix-ui/themes/styles.css'
import './index.css'

import {
  createRouter,
  parseSearchWith,
  RouterProvider,
  stringifySearchWith,
} from '@tanstack/react-router'
import { GeckoAppKit } from 'app-kit'
import { ErrorFallback } from 'pages/errorFallback/ErrorFallback'
import { NoMatch } from 'pages/noMatch/NoMatch'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { Cache, SWRConfig, useSWRConfig } from 'swr'

import { getForecastRuntimeVariables } from '../config'
import { routeTree } from './routeTree.gen'

const runtimeVariables = getForecastRuntimeVariables()

// Exported for use by RouteContextSpanProcessor to add route params to spans
export const router = createRouter({
  routeTree,
  context: {
    cache: {} as Cache,
  },
  basepath: runtimeVariables.BASE_PATH,
  // Same defaults as the router, but explicit: array/object search values round-trip
  // as JSON per key (see TanStack “Custom Search Param Serialization”).
  parseSearch: parseSearchWith(JSON.parse),
  stringifySearch: stringifySearchWith(JSON.stringify, JSON.parse),
  defaultNotFoundComponent: () => <NoMatch />,
  defaultErrorComponent: ({ error }) => (
    <ErrorFallback reset={() => {}} error={error} />
  ),
})

// This Typescript magic ensures that the route tree's parameter types are
// available everywhere in the app. See:
// https://tanstack.com/router/v1/docs/framework/react/api/router/RegisterType
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// This declaration ensures that runtime variables are appropriately typed
// using similar Typescript magic to the Tanstack router declaration above
declare module 'app-kit' {
  interface GeckoAppConfig {
    runtimeVariables: typeof runtimeVariables
  }
}

// Reuse the root across vite HMR re-runs of this entry module — a second
// createRoot on the same container appends a second copy of the whole app
// (visible as the UI repeating down the page after a few hot updates).
const rootContainer = document.getElementById('root') as HTMLElement & {
  __reactRoot?: ReactDOM.Root
}
const reactRoot = (rootContainer.__reactRoot ??=
  ReactDOM.createRoot(rootContainer))
reactRoot.render(
  <React.StrictMode>
    <GeckoAppKit config={{ appName: 'Forecast', runtimeVariables }}>
      <SWRConfig value={{ shouldRetryOnError: false, errorRetryCount: 1 }}>
        <Routes />
      </SWRConfig>
    </GeckoAppKit>
  </React.StrictMode>
)

function Routes() {
  const { cache } = useSWRConfig()
  return <RouterProvider router={router} context={{ cache }} />
}
